import { useEffect, useState } from "react";
import { Zap, Send, X, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Iter389 · Feature 3 — Organiser-side early access banner + request
// modal. Renders on FestivalDetail. When there's no pending request:
// shows the calculated unlock date/time + a "Request extended early
// access" button. When pending: shows status pill. When decided:
// shows the outcome banner.
export default function EarlyAccessBanner({ festivalId, startAt }) {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Iter463 · Fix 1 — Once the festival's start date has passed, the
  // early access window has already elapsed and there is nothing left
  // to request. Hide the request CTA (and defensively the modal) for
  // every role so no one is offered a dead option.
  const festivalStarted = (() => {
    if (!startAt) return false;
    try { return new Date(startAt).getTime() < Date.now(); } catch { return false; }
  })();

  const reload = async () => {
    try {
      const r = await api.get(`/festival/${festivalId}/early-access`);
      setState(r.data);
    } catch { setState({ unlock_at: null }); }
  };
  useEffect(() => {
    if (!festivalId) return undefined;
    reload();
    const t = setInterval(reload, 30000);
    const onFocus = () => { if (document.visibilityState === "visible") reload(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
  }, [festivalId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/festival/${festivalId}/early-access-request`, { reason: reason.trim() });
      toast.success("Request sent — Rally-Lab has been notified");
      setOpen(false);
      setReason("");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  if (!state) return null;
  const unlock = state.unlock_at ? new Date(state.unlock_at) : null;
  const pending = state.pending_request;
  const latest = state.latest_request;
  return (
    <>
      <div className="mb-4 border border-[#66C2FF] bg-[#0F2A4B]/30 p-3" data-testid="ea-banner">
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <div>
            <div className="label-mono text-[#66C2FF] text-[10px]">EARLY ACCESS WINDOW</div>
            {unlock ? (
              <div className="text-sm text-white mt-1" data-testid="ea-banner-unlock">
                Your early access window opens at
                <b className="text-[#CCFF00]"> {unlock.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</b>
                {" on "}
                <b className="text-[#CCFF00]">{unlock.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</b>
                {" "}({state.effective_hours}h before festival day-1 midnight
                {state.override_active ? ", per-festival override" : ""}).
              </div>
            ) : (
              <div className="text-sm text-[#A3A3A3] mt-1">Set the festival start date to see the window.</div>
            )}
          </div>
          {!pending && !festivalStarted && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="ea-request-open"
              className="text-[10px] uppercase tracking-widest border border-[#CCFF00] text-[#CCFF00] px-2 py-1 hover:bg-[#CCFF00] hover:text-black"
            >
              Request extended early access
            </button>
          )}
          {pending && (
            <span className="text-[10px] uppercase tracking-widest text-[#FFB020] border border-[#FFB020] px-2 py-0.5" data-testid="ea-request-pending">
              Request pending
            </span>
          )}
        </div>
        {latest && latest.status === "approved" && !pending && (
          <div className="text-[11px] text-[#CCFF00] mt-2" data-testid="ea-request-approved">
            <CheckCircle2 className="w-3 h-3 inline mr-1" />
            Last request approved{latest.override_hours ? ` — window now ${latest.override_hours}h` : ""}.
          </div>
        )}
        {latest && latest.status === "declined" && !pending && (
          <div className="text-[11px] text-[#FF4500] mt-2" data-testid="ea-request-declined">
            Last request declined{latest.decision_reason ? ` — "${latest.decision_reason}"` : ""}.
          </div>
        )}
      </div>
      {open && !festivalStarted && (
        <div className="fixed inset-0 z-40 bg-black/85 flex items-center justify-center p-4" data-testid="ea-request-modal">
          <div className="w-full max-w-md bg-[#0A0A0A] border-2 border-[#CCFF00] p-5">
            <div className="flex items-start justify-between gap-2 mb-3">
              <div>
                <div className="label-mono text-[#CCFF00] text-[10px]">EARLY ACCESS · REQUEST</div>
                <h2 className="h-display text-xl mt-1">Ask Rally-Lab for more setup time</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-[#A3A3A3] hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-[#A3A3A3] mb-3">
              Current window: <b className="text-white">{state.effective_hours}h</b>. Tell Rally-Lab why you need more (max 300 chars).
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 300))}
              rows={4}
              placeholder="e.g. Big check-in queue, expecting 200 walk-ins — need extra hours to set up courts and QR posters."
              data-testid="ea-request-reason"
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white text-sm focus:border-[#CCFF00] focus:outline-none"
            />
            <div className="text-[10px] text-[#525252] text-right mb-3">{reason.length}/300</div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                data-testid="ea-request-cancel"
                className="px-4 py-2 text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={busy || !reason.trim()}
                data-testid="ea-request-submit"
                className="px-4 py-2 text-xs uppercase tracking-widest bg-[#CCFF00] text-black hover:opacity-80 disabled:opacity-40"
              >
                <Send className="w-3.5 h-3.5 inline mr-1" /> {busy ? "Sending…" : "Send request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
